import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  LinearProgress,
  Tooltip,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import globals from '../globals';
import RedButton from '../components/redButton';
import ExamQuizPage from '../exam_quiz';

// Flatten sections -> a single question list in the quiz's expected shape.
function buildQuizData(qData) {
  const questions = (qData.sections || []).flatMap((s) => s.questions || []);
  return {
    title: qData.title,
    description: qData.description,
    introTitle: qData.intro_title,
    introDescription: qData.intro_description,
    guidelines: qData.guidelines || [],
    durationMinutes: qData.total_duration_minutes || null,
    // Server-computed remaining time (survives refreshes); null falls back to full duration.
    remainingSeconds: typeof qData.remaining_seconds === 'number' ? qData.remaining_seconds : null,
    questions: questions.map((q) => ({
      id: String(q.id),
      apiType: q.type,
      type: q.type === 'multiple_choice' ? 'single' : q.type === 'multi_part' ? 'multi' : 'file',
      text: q.text || '',
      imageSrc: q.image_url || null,
      points: q.points || 1,
      options: (q.options || []).map((o) => ({ id: String(o.id), label: o.text })),
      subquestions: (q.subquestions || []).map((sq) => ({ id: String(sq.id), text: sq.text || '' })),
    })),
  };
}

// Map the backend's saved draft answers back into the quiz's state shape.
function buildInitialAnswers(qData) {
  const selected = {};
  const subAnswers = {};
  const files = {};
  const saved = qData.saved_answers || {};
  for (const [qid, entry] of Object.entries(saved)) {
    if (!entry) continue;
    if (entry.choice_id) selected[qid] = String(entry.choice_id);
    if (entry.sub_answers) {
      for (const [subId, val] of Object.entries(entry.sub_answers)) {
        subAnswers[`${qid}::${subId}`] = val;
      }
    }
    if (Array.isArray(entry.files) && entry.files.length > 0) {
      files[qid] = entry.files;
    }
  }
  return { selected, subAnswers, files };
}

function ExamSessionPage() {
  const { examId, sessionId } = useParams();
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [data, setData] = useState(null);
  const [quizData, setQuizData] = useState(null);
  const [initialAnswers, setInitialAnswers] = useState(null);
  const [starting, setStarting] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate(`/login?redirect=/exams/${examId}/${sessionId}`);
    }
  }, [authLoading, user, navigate, examId, sessionId]);

  useEffect(() => {
    if (authLoading || !user || !token) return;

    fetch(`${globals.apiUrl}/exams/${examId}/${sessionId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
      .then((res) => {
        if (res.status === 404) {
          setErrorMessage('Невалидна сесия. Провери линка от имейла.');
          setHasError(true);
          setIsLoading(false);
          return null;
        }
        if (res.status === 403) {
          setErrorMessage('Нямаш достъп до тази сесия.');
          setHasError(true);
          setIsLoading(false);
          return null;
        }
        if (!res.ok) {
          setErrorMessage('Възникна грешка при зареждане на изпита.');
          setHasError(true);
          setIsLoading(false);
          return null;
        }
        return res.json();
      })
      .then((json) => {
        if (!json) return;
        setData(json);
        setAlreadySubmitted(Boolean(json.already_submitted));
        setIsLoading(false);
      })
      .catch(() => {
        setErrorMessage('Възникна грешка при зареждане на изпита. Моля свържете се с екипа на eAcademiaBg.');
        setHasError(true);
        setIsLoading(false);
      });
  }, [authLoading, user, token, examId, sessionId]);

  async function handleStart() {
    setStarting(true);
    try {
      const res = await fetch(`${globals.apiUrl}/exams/${examId}/${sessionId}/questions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        setErrorMessage('Възникна грешка при зареждане на въпросите.');
        setHasError(true);
        setStarting(false);
        return;
      }
      const qData = await res.json();
      if (qData.already_submitted) {
        setAlreadySubmitted(true);
        setStarting(false);
        return;
      }
      setInitialAnswers(buildInitialAnswers(qData));
      setQuizData(buildQuizData(qData));
    } catch {
      setErrorMessage('Възникна грешка при зареждане на въпросите.');
      setHasError(true);
    }
    setStarting(false);
  }

  // Build the answers payload. Files are already-uploaded { name, url } entries
  // (uploaded on attach via handleUploadFile), so nothing is uploaded here.
  function buildAnswers({ selected, files, subAnswers }) {
    const answers = {};
    for (const q of quizData.questions) {
      const entry = { type: q.apiType };
      if (q.type === 'single') {
        entry.choice_id = selected[q.id] || null;
      } else if (q.type === 'multi') {
        entry.sub_answers = {};
        for (const sq of q.subquestions) {
          entry.sub_answers[sq.id] = subAnswers[`${q.id}::${sq.id}`] || '';
        }
      } else if (q.type === 'file') {
        entry.files = (files[q.id] || [])
          .filter((f) => f && f.url)
          .map((f) => ({ name: f.name, url: f.url }));
      }
      answers[q.id] = entry;
    }
    return answers;
  }

  // Upload one answer file to S3 (under exams/<examId>/sessions/<sessionId>/).
  const handleUploadFile = useCallback(async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${globals.apiUrl}/exams/${examId}/${sessionId}/files/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    if (!res.ok) throw new Error('upload failed');
    return res.json(); // { url, name }
  }, [examId, sessionId, token]);

  async function handleSubmitExam(payload) {
    const answers = buildAnswers(payload);
    await fetch(`${globals.apiUrl}/exams/${examId}/${sessionId}/submit`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ answers }),
    });
  }

  // Autosave in-progress answers (files are already uploaded, referenced by URL).
  const handleSaveDraft = useCallback(async (payload) => {
    if (!quizData) return;
    const answers = buildAnswers(payload);
    try {
      await fetch(`${globals.apiUrl}/exams/${examId}/${sessionId}/save`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers }),
      });
    } catch { /* autosave is best-effort; ignore errors */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizData, examId, sessionId, token]);

  if (authLoading || isLoading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 6 }}>
          <Typography variant="h5" gutterBottom>
            Зареждане...
          </Typography>
          <LinearProgress />
        </Box>
      </Container>
    );
  }

  // A draft exists (student started earlier and autosaved) but hasn't submitted.
  const resumeInProgress = Boolean(data?.in_progress) && !alreadySubmitted;
  const startLabel = resumeInProgress ? 'Продължи изпита' : 'Започни изпита';

  // Student is actively taking the exam.
  if (quizData) {
    return (
      <ExamQuizPage
        examData={quizData}
        revealAnswers={false}
        onSubmit={handleSubmitExam}
        onSaveDraft={handleSaveDraft}
        onUploadFile={handleUploadFile}
        initialAnswers={initialAnswers}
        initialRemainingSeconds={quizData.remainingSeconds}
        autoStart={resumeInProgress}
        startButtonText={startLabel}
      />
    );
  }

  if (hasError) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 6 }}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" color="error" gutterBottom>
                <WarningIcon sx={{ mb: -0.4, mr: 1 }} />
                Невалидна сесия
              </Typography>
              <Typography variant="body1">{errorMessage}</Typography>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  const isOnDemand = data?.exam_type === 'ON_DEMAND';
  const startDateObj = data?.start_date ? new Date(data.start_date) : null;
  const dateStr = startDateObj
    ? startDateObj.toLocaleDateString('bg-BG', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '';
  const timeStr = startDateObj
    ? startDateObj.toLocaleTimeString('bg-BG', { hour: '2-digit', minute: '2-digit' })
    : '';

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Grid container direction="column" spacing={3}>
        <Grid item>
          <Typography variant="h4" component="h1" gutterBottom>
            {data.title}
          </Typography>
        </Grid>

        <Grid item>
          <Card elevation={2}>
            <CardContent>
              <Grid container direction="column" spacing={2}>
                <Grid item>
                  <Typography variant="h6">
                    <WarningIcon sx={{ mb: -0.4, mr: 1 }} />
                    {data.title}
                  </Typography>
                </Grid>

                <Grid item>
                  {isOnDemand ? (
                    <Typography variant="body1" sx={{ color: globals.colors.darkBlue, fontWeight: 'bold', fontSize: 17 }}>
                      Изпитът е достъпен по всяко време. Можеш да го започнеш когато си готов.
                    </Typography>
                  ) : (
                    <Typography variant="body1" sx={{ color: globals.colors.darkBlue, fontWeight: 'bold', fontSize: 17 }}>
                      Изпитът ще се отключи на <strong>{dateStr}</strong> в <strong>{timeStr}ч.</strong> Моля, влез в платформата навреме и се увери, че имаш стабилна интернет връзка и включена камера в Zoom.
                    </Typography>
                  )}
                </Grid>

                {alreadySubmitted && (
                  <Grid item>
                    <Typography variant="body1" sx={{ color: globals.colors.darkGreen, fontWeight: 'bold' }}>
                      Вече си предал този изпит. Отговорите ти предстои да бъдат оценени.
                    </Typography>
                  </Grid>
                )}

                {resumeInProgress && (
                  <Grid item>
                    <Typography variant="body1" sx={{ color: globals.colors.darkBlue, fontWeight: 'bold' }}>
                      Имаш започнат изпит. Можеш да продължиш оттам, докъдето си стигнал.
                    </Typography>
                  </Grid>
                )}

                <Grid item>
                  <Grid container justifyContent="flex-end" sx={{ mt: 1 }}>
                    <Grid item>
                      {isOnDemand ? (
                        <RedButton
                          text={starting ? 'Зареждане...' : startLabel}
                          disabled={starting || alreadySubmitted}
                          onClick={handleStart}
                        />
                      ) : startDateObj && new Date() >= startDateObj ? (
                        <RedButton
                          text={starting ? 'Зареждане...' : startLabel}
                          disabled={starting || alreadySubmitted}
                          onClick={handleStart}
                        />
                      ) : (
                        <Tooltip title="Изпитът ще бъде достъпен от датата на провеждане">
                          <span>
                            <RedButton text={startLabel} disabled onClick={() => { }} />
                          </span>
                        </Tooltip>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}

export default ExamSessionPage;
